/**
 * スタートガイドの紙芝居（1 枚ずつ送る）。
 *
 * ビュー側の約束（<c>SiteApp/pages/start-guide-page.tsx</c> が書き出す）:
 *   [data-start-guide]        全体。動き出したら is-slideshow を付ける（CSS が今の 1 枚だけを出す）
 *   [data-guide-chapter]      章（section。id が chapter-1 など。値は章の名前）。中の .guide-slide が 1 枚
 *   [data-guide-prev] / [data-guide-next]  前へ・次へ（上と下に 1 組ずつ）
 *   [data-guide-status]       今が何枚目か（「3 / 43・2. ポータルへのログイン」）
 *   [data-guide-controls]     前へ・次へを入れた行。<b>スクリプトが動くまで隠してある</b>
 *   [data-guide-toc]          目次のボタン（値は章の id）。今の章に aria-current を付ける
 *   [data-guide-print]        印刷する（スクリプトが動くまで隠してある）
 *
 * <b>今の 1 枚は URL に残す</b>（#chapter-2-3 = 2 章の 3 枚目。#chapter-2 は 2 章の 1 枚目）。読み直し・共有で同じ 1 枚が開く。
 * 送るたびに履歴を積まない（replaceState）——「戻る」で 1 枚ずつ戻ると、ガイドから出られなくなる。
 */

/** 1 枚。 */
export interface Slide {
    readonly element: HTMLElement;
    readonly chapter: HTMLElement;
    readonly id: string;
}

/** 文字を打っている欄では矢印を奪わない。 */
const isTyping = (target: EventTarget | null): boolean =>
    target instanceof HTMLElement && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

/** URL の # から 1 枚を探す。<b>知らない値なら 0（最初の 1 枚）</b>。 */
export function slideIndexOf(slides: readonly Slide[], hash: string): number {
    const id = decodeURIComponent(hash.replace(/^#/u, ""));
    if (id === "") {
        return 0;
    }
    const exact = slides.findIndex((slide) => slide.id === id);
    if (exact !== -1) {
        return exact;
    }
    // 章の id（#chapter-2）なら、その章の 1 枚目
    const chapter = slides.findIndex((slide) => slide.chapter.id === id);
    return Math.max(chapter, 0);
}

/** 章ごとに 1 枚ずつ id を振って並べる（chapter-2 の 3 枚目は chapter-2-3）。 */
function collectSlides(root: HTMLElement): Slide[] {
    return [...root.querySelectorAll<HTMLElement>("[data-guide-chapter]")].flatMap((chapter) =>
        [...chapter.querySelectorAll<HTMLElement>(".guide-slide")].map((element, index) => {
            const id = `${chapter.id}-${String(index + 1)}`;
            element.id = id;
            // 写真は押すと拡大する（media/image-zoom.ts の印。中身の HTML には data-* を書けないので、ここで付ける）。
            // 狭い画面では、パソコンの画面の写真の文字が小さくて読めない
            for (const image of element.querySelectorAll("img")) image.dataset["zoom"] = "";
            return { element, chapter, id };
        }));
}

export const setupStartGuide = (root: ParentNode = document): void => {
    const guide = root.querySelector<HTMLElement>("[data-start-guide]");
    if (guide === null) {
        return;
    }
    const slides = collectSlides(guide);
    if (slides.length === 0) {
        return;
    }

    const all = <T extends HTMLElement>(selector: string): T[] => [...guide.querySelectorAll<T>(selector)];
    const prev = all<HTMLButtonElement>("[data-guide-prev]");
    const next = all<HTMLButtonElement>("[data-guide-next]");
    const viewer = guide.querySelector<HTMLElement>("[data-guide-viewer]");
    let current = -1;

    const show = (index: number, options: { readonly updateUrl: boolean; readonly scroll: boolean }): void => {
        const target = Math.min(Math.max(index, 0), slides.length - 1);
        const slide = slides[target];
        if (slide === undefined) {
            return;
        }
        current = target;
        for (const other of slides) {
            other.element.classList.toggle("is-current", other === slide);
            other.chapter.classList.toggle("is-current", other.chapter === slide.chapter);
        }
        for (const button of prev) button.disabled = target === 0;
        for (const button of next) button.disabled = target === slides.length - 1;
        for (const status of all("[data-guide-status]")) {
            status.textContent = `${String(target + 1)} / ${String(slides.length)}・${slide.chapter.dataset["guideChapter"] ?? ""}`;
        }
        for (const link of all("[data-guide-toc]")) {
            link.setAttribute("aria-current", String(link.dataset["guideToc"] === slide.chapter.id));
        }
        if (options.updateUrl) {
            window.history.replaceState(window.history.state, "", `#${slide.id}`);
        }
        // 送ったあとも紙芝居の上端が見えるようにする（下の「次へ」で送ると、次の 1 枚の途中から始まる）
        if (options.scroll && viewer !== null && viewer.getBoundingClientRect().top < 0) {
            viewer.scrollIntoView({ block: "start" });
        }
    };

    const step = (delta: number): void => {
        show(current + delta, { updateUrl: true, scroll: true });
    };
    for (const button of prev) button.addEventListener("click", () => { step(-1); });
    for (const button of next) button.addEventListener("click", () => { step(1); });

    // 目次のボタンは章の id へ飛ぶ。# が変わったら、その章の 1 枚目を出す
    window.addEventListener("hashchange", () => {
        show(slideIndexOf(slides, location.hash), { updateUrl: false, scroll: false });
    });

    document.addEventListener("keydown", (event) => {
        // 拡大の表示（PhotoSwipe）が開いている間は、矢印はそちらのもの
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || isTyping(event.target) || document.querySelector(".pswp") !== null) {
            return;
        }
        if (event.key === "ArrowLeft") {
            step(-1);
            event.preventDefault();
        } else if (event.key === "ArrowRight") {
            step(1);
            event.preventDefault();
        }
    });

    for (const button of all<HTMLButtonElement>("[data-guide-print]")) {
        button.addEventListener("click", () => { window.print(); });
    }
    for (const hidden of all("[data-guide-controls], [data-guide-print]")) {
        hidden.hidden = false;
    }

    guide.classList.add("is-slideshow");
    show(slideIndexOf(slides, location.hash), { updateUrl: false, scroll: false });
};
